import PaymentService from '../services/paymentService';

// Removed caching - always fetch fresh data to avoid stale data issues
// Function to invalidate cache (kept for compatibility but does nothing)
export const invalidateServicesCache = (clientId = null) => {
  // No-op: caching removed
};

function buildSuggestAllocationsConfig() {
  return {
    fetch: async (formData) => {
      const clientId = formData.client_id;
      const amount = parseFloat(formData.amount);
      const paymentDate = formData.payment_date;
      if (!clientId || Number.isNaN(amount) || amount <= 0 || !paymentDate) {
        return {
          ok: false,
          error: 'Συμπληρώστε πελάτη, έγκυρο ποσό και ημερομηνία πληρωμής.',
        };
      }
      const result = await PaymentService.suggestAllocations({
        clientId: parseInt(clientId, 10),
        amount,
        paymentDate,
        excludePaymentId: formData.id ? parseInt(formData.id, 10) : null,
      });
      if (!result.success) {
        return { ok: false, error: result.error || 'Αποτυχία πρότασης' };
      }
      return {
        ok: true,
        allocations: result.allocations,
        leftoverUnallocated: parseFloat(result.leftoverUnallocated) || 0,
      };
    },
  };
}

/** Νεότερες περίοδοι πρώτα (start_date φθίνουσα), μετά id· χωρίς ημερομηνία στο τέλος. */
function sortServicesForAllocationDropdown(services) {
  if (!services?.length) return [];
  const startMs = (s) => {
    if (!s.start_date) return null;
    const t = new Date(s.start_date).getTime();
    return Number.isNaN(t) ? null : t;
  };
  return [...services].sort((a, b) => {
    const ta = startMs(a);
    const tb = startMs(b);
    if (ta != null && tb != null && tb !== ta) return tb - ta;
    if (ta != null && tb == null) return -1;
    if (ta == null && tb != null) return 1;
    return (b.id || 0) - (a.id || 0);
  });
}

function formatServicePeriodInLabel(service) {
  const bp = service.billing_period;
  if (!bp || bp === 'one_time' || (!service.start_date && !service.end_date)) {
    return '';
  }
  const fmt = (d) => {
    if (!d) return '—';
    const parsed = new Date(d);
    if (Number.isNaN(parsed.getTime())) return String(d);
    return parsed.toLocaleDateString('el-GR', {
      day: 'numeric',
      month: 'numeric',
      year: 'numeric'
    });
  };
  return ` (${fmt(service.start_date)}-${fmt(service.end_date)})`;
}

/**
 * Επιλογές υπηρεσιών για κατανομή (ταξινόμηση + ετικέτα με περίοδο για συνδρομές).
 */
async function loadAllocationServiceSelectOptions(formData) {
  const clientId = formData.client_id;
  if (!clientId) {
    return [];
  }
  try {
    const response = await PaymentService.getClientServices(clientId);
    if (!response.success || !response.data) {
      return [];
    }
    const services = sortServicesForAllocationDropdown(response.data);
    if (!services.length) {
      return [];
    }

    const currentAllocations = formData.allocations || [];
    const allocationsByServiceId = new Map();
    currentAllocations.forEach((alloc) => {
      const serviceId = parseInt(alloc.service_id, 10);
      if (!Number.isNaN(serviceId)) {
        const amount = parseFloat(alloc.allocated_amount) || 0;
        allocationsByServiceId.set(serviceId, (allocationsByServiceId.get(serviceId) || 0) + amount);
      }
    });

    const originalAllocationsByServiceId = new Map();
    const origAllocSource =
      formData._originalAllocations || (formData.id ? currentAllocations : null);
    if (formData.id && origAllocSource) {
      origAllocSource.forEach((alloc) => {
        const serviceId = parseInt(alloc.service_id, 10);
        if (!Number.isNaN(serviceId)) {
          const amount = parseFloat(alloc.allocated_amount) || 0;
          originalAllocationsByServiceId.set(
            serviceId,
            (originalAllocationsByServiceId.get(serviceId) || 0) + amount
          );
        }
      });
    }

    const selectedServiceIds = new Set(
      currentAllocations
        .map((alloc) => parseInt(alloc.service_id, 10))
        .filter((id) => id && !Number.isNaN(id))
    );

    return services.map((service) => {
      const serviceId = service.id;
      const paidAmount = parseFloat(service.paid_amount) || 0;
      const price = parseFloat(service.price) || 0;
      const currentAllocatedToThisService = allocationsByServiceId.get(serviceId) || 0;

      let livePaidAmount = paidAmount;
      if (formData.id && originalAllocationsByServiceId.has(serviceId)) {
        livePaidAmount -= originalAllocationsByServiceId.get(serviceId);
      }
      livePaidAmount += currentAllocatedToThisService;

      const liveRemaining = price - livePaidAmount;
      const period = formatServicePeriodInLabel(service);
      const priceStr = PaymentService.formatAmount(price);

      let balanceSuffix = '';
      if (liveRemaining <= 0) {
        balanceSuffix = ' ✅';
      } else if (liveRemaining < price) {
        balanceSuffix = ` (Υπόλ: ${PaymentService.formatAmount(liveRemaining)})`;
      }

      const label = `${service.name}${period} - ${priceStr}${balanceSuffix}`;

      return {
        value: serviceId,
        label,
        disabled: selectedServiceIds.has(serviceId) || liveRemaining <= 0
      };
    });
  } catch (error) {
    console.error('Error loading services:', error);
    return [];
  }
}

// Payment Form Schema για FormBuilder
export const createPaymentSchema = {
  title: 'Δημιουργία Νέας Πληρωμής',
  
  initialValues: {
    client_id: '',
    amount: '',
    payment_method: 'bank_transfer',
    payment_date: new Date().toISOString().split('T')[0],
    notes: '',
    allocations: []
  },
  
  validationRules: {
    client_id: {
      required: true,
      message: 'Ο πελάτης είναι υποχρεωτικός'
    },
    amount: {
      required: true,
      validate: (value) => {
        const numValue = parseFloat(value);
        if (isNaN(numValue) || numValue <= 0) {
          return 'Το ποσό πρέπει να είναι μεγαλύτερο από 0';
        }
        return null;
      },
      message: 'Το ποσό είναι υποχρεωτικό και πρέπει να είναι μεγαλύτερο από 0'
    },
    payment_method: {
      required: true,
      message: 'Ο τρόπος πληρωμής είναι υποχρεωτικός'
    },
    payment_date: {
      required: true,
      message: 'Η ημερομηνία πληρωμής είναι υποχρεωτική'
    },
    allocations: {
      validate: (allocations, formData) => {
        if (!allocations || allocations.length === 0) return null;
        
        // Check for duplicate services
        const serviceIds = allocations
          .map(alloc => alloc.service_id)
          .filter(id => id && !isNaN(id));
        
        const uniqueServiceIds = [...new Set(serviceIds)];
        if (serviceIds.length !== uniqueServiceIds.length) {
          return 'Δεν μπορείτε να επιλέξετε την ίδια υπηρεσία σε πολλαπλές κατανομές';
        }
        
        // Check total allocation amount
        const totalAllocated = allocations.reduce((sum, alloc) => sum + (parseFloat(alloc.allocated_amount) || 0), 0);
        const paymentAmount = parseFloat(formData.amount) || 0;
        
        // Add tolerance for floating point precision
        const tolerance = 0.01;
        if (totalAllocated > paymentAmount + tolerance) {
          return 'Το συνολικό ποσό των κατανομών δεν μπορεί να ξεπερνά το ποσό της πληρωμής';
        }
        
        return null;
      }
    }
  },
  
  sections: [
    {
      title: 'Στοιχεία Πληρωμής',
      fields: [
        {
          name: 'client_id',
          type: 'client_select',
          label: 'Πελάτης',
          required: true,
          placeholder: 'Επιλέξτε ή αναζητήστε πελάτη...',
          validation: {
            required: 'Ο πελάτης είναι υποχρεωτικός'
          }
        },
        {
          name: 'amount',
          type: 'number',
          label: 'Ποσό',
          required: true,
          placeholder: '0.00',
          min: 0.01,
          step: 0.01,
          validation: {
            required: 'Το ποσό είναι υποχρεωτικό',
            min: 'Το ποσό πρέπει να είναι μεγαλύτερο από 0'
          }
        },
        {
          name: 'payment_method',
          type: 'select',
          label: 'Τρόπος Πληρωμής',
          required: true,
          options: [
            { value: 'cash', label: 'Μετρητά' },
            { value: 'bank_transfer', label: 'Τραπεζική Κατάθεση' },
            { value: 'card', label: 'Κάρτα' },
            { value: 'check', label: 'Επιταγή' },
            { value: 'other', label: 'Άλλο' }
          ],
          validation: {
            required: 'Ο τρόπος πληρωμής είναι υποχρεωτικός'
          }
        },
        {
          name: 'payment_date',
          type: 'date',
          label: 'Ημερομηνία Πληρωμής',
          required: true,
          defaultValue: new Date().toISOString().split('T')[0],
          validation: {
            required: 'Η ημερομηνία πληρωμής είναι υποχρεωτική'
          }
        },
        {
          name: 'notes',
          type: 'textarea',
          label: 'Σημειώσεις',
          required: false,
          placeholder: 'Επιπλέον πληροφορίες...',
          rows: 3
        },
        {
          name: 'allocations',
          type: 'dynamic-array',
          label: 'Κατανομή σε Υπηρεσίες',
          required: false,
          description:
            'Κατανομή σε υπηρεσίες (προαιρετικό). Στο ίδιο βήμα συμπληρώνετε ποσό πληρωμής και, αν θέλετε, γραμμές κατανομής. Το άθροισμα κατανομών μπορεί να είναι μικρότερο από το ποσό πληρωμής — το υπόλοιπο μένει ως κατάθεση/υπόλοιπο πελάτη. Κουμπί «Πρόταση»: αυτόματη πρόταση κατά υπόλοιπα και ημερομηνίες (ελέγξτε πριν αποθηκεύσετε).',
          suggestAllocations: buildSuggestAllocationsConfig(),
          dependsOn: 'client_id',
          itemSchema: {
            sections: [
              {
                fields: [
                  {
                    name: 'service_id',
                    type: 'select',
                    label: 'Υπηρεσία',
                    required: true,
                    placeholder: 'Επιλέξτε υπηρεσία',
                    options: [], // Will be populated dynamically
                    size: 12, // Full width row
                    loadOptions: (formData) => loadAllocationServiceSelectOptions(formData)
                  },
                  {
                    name: 'notes',
                    type: 'text',
                    label: 'Σημειώσεις',
                    required: false,
                    placeholder: 'Σημειώσεις για αυτή την κατανομή...',
                    size: 6 // Half width
                  },
                  {
                    name: 'allocated_amount',
                    type: 'number',
                    label: 'Ποσό',
                    required: true,
                    placeholder: '0.00',
                    size: 6, // Half width
                    min: 0.01,
                    step: 0.01,
                    validation: {
                      required: 'Το ποσό είναι υποχρεωτικό',
                      min: 'Το ποσό πρέπει να είναι μεγαλύτερο από 0'
                    }
                  }
                ]
              }
            ]
          },
          validation: {
            validate: (allocations, formData) => {
              if (!allocations || allocations.length === 0) return null;
              
              // Check for duplicate services
              const serviceIds = allocations
                .map(alloc => alloc.service_id)
                .filter(id => id && !isNaN(id));
              
              const uniqueServiceIds = [...new Set(serviceIds)];
              if (serviceIds.length !== uniqueServiceIds.length) {
                return 'Δεν μπορείτε να επιλέξετε την ίδια υπηρεσία σε πολλαπλές κατανομές';
              }
              
              // Check total allocation amount
              const totalAllocated = allocations.reduce((sum, alloc) => sum + (parseFloat(alloc.allocated_amount) || 0), 0);
              const paymentAmount = parseFloat(formData.amount) || 0;
              
              // Add tolerance for floating point precision
              const tolerance = 0.01;
              if (totalAllocated > paymentAmount + tolerance) {
                return 'Το συνολικό ποσό των κατανομών δεν μπορεί να ξεπερνά το ποσό της πληρωμής';
              }
              
              return null;
            }
          }
        }
      ]
    }
  ],
  validation: {
    custom: (data) => {
      const errors = {};
      
      // Validate allocations total
      if (data.allocations && data.allocations.length > 0) {
        const totalAllocated = data.allocations.reduce((sum, alloc) => sum + (parseFloat(alloc.allocated_amount) || 0), 0);
        const paymentAmount = parseFloat(data.amount) || 0;
        
        // Add tolerance for floating point precision
        const tolerance = 0.01;
        if (totalAllocated > paymentAmount + tolerance) {
          errors.allocations = 'Το συνολικό ποσό των κατανομών δεν μπορεί να ξεπερνά το ποσό της πληρωμής';
        }
      }
      
      return {
        isValid: Object.keys(errors).length === 0,
        errors
      };
    }
  }
};

export const updatePaymentSchema = {
  title: 'Επεξεργασία Πληρωμής',
  
  initialValues: {
    client_id: '',
    amount: '',
    payment_method: '',
    payment_date: '',
    notes: '',
    allocations: []
  },
  
  validationRules: {
    client_id: {
      required: true,
      message: 'Ο πελάτης είναι υποχρεωτικός'
    },
    amount: {
      required: true,
      validate: (value) => {
        const numValue = parseFloat(value);
        if (isNaN(numValue) || numValue <= 0) {
          return 'Το ποσό πρέπει να είναι μεγαλύτερο από 0';
        }
        return null;
      },
      message: 'Το ποσό είναι υποχρεωτικό και πρέπει να είναι μεγαλύτερο από 0'
    },
    payment_method: {
      required: true,
      message: 'Ο τρόπος πληρωμής είναι υποχρεωτικός'
    },
    payment_date: {
      required: true,
      message: 'Η ημερομηνία πληρωμής είναι υποχρεωτική'
    },
    allocations: {
      validate: (allocations, formData) => {
        if (!allocations || allocations.length === 0) return null;
        
        // Check for duplicate services
        const serviceIds = allocations
          .map(alloc => alloc.service_id)
          .filter(id => id && !isNaN(id));
        
        const uniqueServiceIds = [...new Set(serviceIds)];
        if (serviceIds.length !== uniqueServiceIds.length) {
          return 'Δεν μπορείτε να επιλέξετε την ίδια υπηρεσία σε πολλαπλές κατανομές';
        }
        
        // Check total allocation amount
        const totalAllocated = allocations.reduce((sum, alloc) => sum + (parseFloat(alloc.allocated_amount) || 0), 0);
        const paymentAmount = parseFloat(formData.amount) || 0;
        
        // Add tolerance for floating point precision
        const tolerance = 0.01;
        if (totalAllocated > paymentAmount + tolerance) {
          return 'Το συνολικό ποσό των κατανομών δεν μπορεί να ξεπερνά το ποσό της πληρωμής';
        }
        
        return null;
      }
    }
  },
  
  sections: [
    {
      title: 'Στοιχεία Πληρωμής',
      fields: [
        {
          name: 'client_id',
          type: 'client_select',
          label: 'Πελάτης',
          required: true,
          placeholder: 'Επιλέξτε ή αναζητήστε πελάτη...',
          validation: {
            required: 'Ο πελάτης είναι υποχρεωτικός'
          }
        },
        {
          name: 'amount',
          type: 'number',
          label: 'Ποσό',
          required: true,
          placeholder: '0.00',
          min: 0.01,
          step: 0.01,
          validation: {
            required: 'Το ποσό είναι υποχρεωτικό',
            min: 'Το ποσό πρέπει να είναι μεγαλύτερο από 0'
          }
        },
        {
          name: 'payment_method',
          type: 'select',
          label: 'Τρόπος Πληρωμής',
          required: true,
          options: [
            { value: 'cash', label: 'Μετρητά' },
            { value: 'bank_transfer', label: 'Τραπεζική Κατάθεση' },
            { value: 'card', label: 'Κάρτα' },
            { value: 'check', label: 'Επιταγή' },
            { value: 'other', label: 'Άλλο' }
          ],
          validation: {
            required: 'Ο τρόπος πληρωμής είναι υποχρεωτικός'
          }
        },
        {
          name: 'payment_date',
          type: 'date',
          label: 'Ημερομηνία Πληρωμής',
          required: true,
          validation: {
            required: 'Η ημερομηνία πληρωμής είναι υποχρεωτική'
          }
        },
        {
          name: 'notes',
          type: 'textarea',
          label: 'Σημειώσεις',
          required: false,
          placeholder: 'Επιπλέον πληροφορίες...',
          rows: 3
        },
        {
          name: 'allocations',
          type: 'dynamic-array',
          label: 'Κατανομή σε Υπηρεσίες',
          required: false,
          description:
            'Κατανομή σε υπηρεσίες (προαιρετικό). Στο ίδιο βήμα συμπληρώνετε ποσό πληρωμής και, αν θέλετε, γραμμές κατανομής. Το άθροισμα κατανομών μπορεί να είναι μικρότερο από το ποσό πληρωμής — το υπόλοιπο μένει ως κατάθεση/υπόλοιπο πελάτη. Κουμπί «Πρόταση»: αυτόματη πρόταση κατά υπόλοιπα και ημερομηνίες (ελέγξτε πριν αποθηκεύσετε).',
          suggestAllocations: buildSuggestAllocationsConfig(),
          dependsOn: 'client_id',
          itemSchema: {
            sections: [
              {
                fields: [
                  {
                    name: 'service_id',
                    type: 'select',
                    label: 'Υπηρεσία',
                    required: true,
                    placeholder: 'Επιλέξτε υπηρεσία',
                    options: [], // Will be populated dynamically
                    size: 12, // Full width row
                    loadOptions: (formData) => loadAllocationServiceSelectOptions(formData)
                  },
                  {
                    name: 'notes',
                    type: 'text',
                    label: 'Σημειώσεις',
                    required: false,
                    placeholder: 'Σημειώσεις για αυτή την κατανομή...',
                    size: 6 // Half width
                  },
                  {
                    name: 'allocated_amount',
                    type: 'number',
                    label: 'Ποσό',
                    required: true,
                    placeholder: '0.00',
                    size: 6, // Half width
                    min: 0.01,
                    step: 0.01,
                    validation: {
                      required: 'Το ποσό είναι υποχρεωτικό',
                      min: 'Το ποσό πρέπει να είναι μεγαλύτερο από 0'
                    }
                  }
                ]
              }
            ]
          },
          validation: {
            validate: (allocations, formData) => {
              if (!allocations || allocations.length === 0) return null;
              
              // Check for duplicate services
              const serviceIds = allocations
                .map(alloc => alloc.service_id)
                .filter(id => id && !isNaN(id));
              
              const uniqueServiceIds = [...new Set(serviceIds)];
              if (serviceIds.length !== uniqueServiceIds.length) {
                return 'Δεν μπορείτε να επιλέξετε την ίδια υπηρεσία σε πολλαπλές κατανομές';
              }
              
              // Check total allocation amount
              const totalAllocated = allocations.reduce((sum, alloc) => sum + (parseFloat(alloc.allocated_amount) || 0), 0);
              const paymentAmount = parseFloat(formData.amount) || 0;
              
              // Add tolerance for floating point precision
              const tolerance = 0.01;
              if (totalAllocated > paymentAmount + tolerance) {
                return 'Το συνολικό ποσό των κατανομών δεν μπορεί να ξεπερνά το ποσό της πληρωμής';
              }
              
              return null;
            }
          }
        }
      ]
    }
  ]
};

// Helper schema για allocations μόνο
export const allocationSchema = {
  title: 'Κατανομή Πληρωμής',
  sections: [
    {
      fields: [
        {
          name: 'service_id',
          type: 'select',
          label: 'Υπηρεσία',
          required: true,
          placeholder: 'Επιλέξτε υπηρεσία',
          options: [], // Will be populated dynamically
          loadOptions: async (clientId) => {
            if (!clientId) return [];
            try {
              const response = await PaymentService.getClientServices(clientId);
              if (!response.success || !response.data?.length) return [];
              const services = sortServicesForAllocationDropdown(response.data);
              return services.map((service) => {
                const period = formatServicePeriodInLabel(service);
                return {
                  value: service.id,
                  label: `${service.name}${period} - ${PaymentService.formatAmount(service.price)}`
                };
              });
            } catch (error) {
              console.error('Error loading services:', error);
              return [];
            }
          }
        },
        {
          name: 'allocated_amount',
          type: 'number',
          label: 'Ποσό',
          required: true,
          placeholder: '0.00',
          min: 0.01,
          step: 0.01,
          validation: {
            required: 'Το ποσό είναι υποχρεωτικό',
            min: 'Το ποσό πρέπει να είναι μεγαλύτερο από 0',
            validate: (value, formData) => {
              const serviceId = formData.service_id;
              const allocatedAmount = parseFloat(value) || 0;
              
              if (!serviceId || allocatedAmount <= 0) {
                return null; // No validation error
              }
              
              // For now, just validate that amount is positive and reasonable
              // The backend will handle the business logic validation
              if (allocatedAmount > 10000) {
                return 'Το ποσό φαίνεται πολύ μεγάλο. Παρακαλώ ελέγξτε το.';
              }
              
              return null; // No validation error
            }
          }
        },
        {
          name: 'notes',
          type: 'text',
          label: 'Σημειώσεις',
          required: false,
          placeholder: 'Σημειώσεις για αυτή την κατανομή...'
        }
      ]
    }
  ]
};